import type { ReactNode } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Check, ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";
import { ArrowLeft, Chart21, NoteRemove, UserEdit } from "iconsax-reactjs";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  BodyText,
  Button,
  Card,
  CardContent,
  HeadingText,
} from "../../../shared/ui";
import { PAGES } from "../../../shared/lib/pageUrlConstants";
import { KybComplianceDetail } from "../types";
import { approvedActionColor } from "./kybShowReviewShared";
import { cn } from "../../../shared/lib";

const countryAccentClassNames: Record<string, string> = {
  Nigeria:
    "bg-[linear-gradient(90deg,#119B56_0%,#119B56_33%,#FFFFFF_33%,#FFFFFF_66%,#119B56_66%,#119B56_100%)]",
  UK: "bg-[#1D4ED8]",
  US: "bg-[#DC2626]",
};

const getStatusBadgeClassName = (statusLabel: string) => {
  const normalizedStatus = statusLabel.trim().toLowerCase();

  if (normalizedStatus === "approved") {
    return "bg-[#DCFDDB] text-[#188B18]";
  }

  if (normalizedStatus === "rejected") {
    return "bg-[#FFE1E1] text-[#C01515]";
  }

  if (normalizedStatus === "started" || normalizedStatus === "pending") {
    return "bg-[#FFEFC6] text-[#B64607]";
  }

  if (
    normalizedStatus === "submitted" ||
    normalizedStatus === "under review"
  ) {
    return "bg-[#EBE4FF] text-[#5E01D6]";
  }

  return "bg-[#EEF1FB] text-[#383B56]";
};

const DetailItem = ({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: ReactNode;
  valueClassName?: string;
}) => (
  <div className="flex min-w-0 min-h-[64px] flex-col gap-1 self-start">
    <BodyText className="aku-body-small-regular text-[#6F739B]">
      {label}
    </BodyText>
    <div
      className={
        valueClassName ??
        "flex min-h-[24px] w-full min-w-0 items-start whitespace-normal break-words aku-body-small-regular text-[#161827]"
      }
    >
      {value}
    </div>
  </div>
);

const truncateText = (value: string, maxLength = 44) => {
  if (value.length <= maxLength) return value;
  return `${value.slice(0, maxLength).trim()}...`;
};

const getHighRiskSummary = (detail: KybComplianceDetail) => {
  if (typeof detail.isHighRisk !== "boolean") return "-";

  if (!detail.isHighRisk) return "No";

  const activities = detail.highRiskActivities
    .map((activity) => activity.trim())
    .filter(Boolean)
    .join(", ");

  return activities ? `Yes • ${truncateText(activities)}` : "Yes";
};

const KybComplianceShowTopSection = ({
  detail,
  canGoToPrevious,
  canGoToNext,
  canFinishReview,
  isReadOnly = false,
  onGoToPrevious,
  onGoToNext,
  onRejectCustomer,
  onEscalateCustomer,
  onFinishReview,
  onOpenActivityLog,
  onRequestAdditionalInfo,
  onRequestInformationUpdate,
  showExtraInfoRequestBtn,
  isApprovedBusinesses,
}: {
  detail: KybComplianceDetail;
  canGoToPrevious?: boolean;
  canGoToNext?: boolean;
  canFinishReview?: boolean;
  isReadOnly?: boolean;
    showExtraInfoRequestBtn?: boolean;
    isApprovedBusinesses?: boolean;
  onGoToPrevious?: () => void;
  onGoToNext?: () => void;
  onRejectCustomer?: () => void;
  onEscalateCustomer?: () => void;
  onFinishReview?: () => void;
  onOpenActivityLog?: () => void;
  onRequestAdditionalInfo?: () => void;
  onRequestInformationUpdate?: () => void;
}) => {
  const navigate = useNavigate();
  const displayName = detail.businessName || detail.customerName;
  const initials = displayName
    .split(" ")
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
  const navigationButtonClassName =
    "grid h-8 w-8 place-items-center rounded-full  bg-white text-[#161827] transition-colors hover:bg-[#F7F8FD] disabled:cursor-not-allowed disabled:border-[#EEF1FB] disabled:bg-[#F7F8FD] disabled:text-[#B8BDD9] disabled:hover:bg-[#F7F8FD]";


  const moreButtonDropDown = () => (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="grid h-9 w-9 place-items-center rounded-full text-[#2B2F49] hover:bg-[#F3F4FF]"
        >
          <MoreHorizontal size={20} />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={10}
          className="z-[120] min-w-[260px] rounded-[20px] border border-[#E1E6FF] bg-white p-2 shadow-[0_20px_45px_rgba(15,20,40,0.15)]"
        >
          <DropdownMenu.Item
            onSelect={() => showExtraInfoRequestBtn && onRequestAdditionalInfo?.()}
            className={cn("flex cursor-pointer items-center gap-3 rounded-[14px] px-3 py-3 aku-body-normal-medium text-[#383B56] outline-none transition-colors focus:bg-[#F5F6FD]", showExtraInfoRequestBtn ? 'opacity-100 pointer-events-auto' : 'opacity-50 pointer-events-none')}
          >
            <NoteRemove size={25} color="#6F739B" />
            Request additional information
          </DropdownMenu.Item>
          <DropdownMenu.Item
            onSelect={() => {
              isApprovedBusinesses && onRequestInformationUpdate?.()
            }}
            className={cn("flex cursor-pointer items-center gap-3 rounded-[14px] px-3 py-3 aku-body-normal-medium text-[#383B56] outline-none transition-colors focus:bg-[#F5F6FD]", isApprovedBusinesses ? 'opacity-100 pointer-events-auto' : 'opacity-50 pointer-events-none')}
          >
            <UserEdit size={25} color="#6F739B" />
            Request information update
          </DropdownMenu.Item>
          <DropdownMenu.Item
            onSelect={onOpenActivityLog}
            className="flex cursor-pointer items-center gap-3 rounded-[14px] px-3 py-3 aku-body-normal-medium text-[#383B56] outline-none transition-colors focus:bg-[#F5F6FD]"
          >
            <span className="grid h-9 w-9 place-items-center rounded-[10px] border border-[#7A7FAA] text-[#6F739B]">
              <Chart21 size={20} color="#6F739B" />
            </span>
            Activity log
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )

  return (
    <section className="w-full">
      <div className="flex items-start justify-between gap-3">
        <button
          type="button"
          onClick={() => navigate(PAGES.kybCompliance)}
          className="inline-flex items-center gap-2"
        >
          <ArrowLeft size={20} color="#6F739B" />
          <span className="aku-body-small-medium text-[#6F00FF]">Back</span>
        </button>

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onGoToPrevious}
            disabled={!canGoToPrevious}
            className={navigationButtonClassName}
          >
            <ChevronLeft size={20} />
          </button>
          <button
            type="button"
            onClick={onGoToNext}
            disabled={!canGoToNext}
            className={navigationButtonClassName}
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>

      <div className="mt-5 flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex items-center gap-3">
          <div className="grid h-14 w-14 place-items-center rounded-full bg-[#EFF0FA] text-[18px] font-[500] text-[#383B56]">
            {initials}
          </div>

          <div>
            <div className="flex flex-wrap items-center gap-2.5">
              <HeadingText className="aku-heading-h2-medium text-[#161827]">
                {displayName}
              </HeadingText>
              <Badge
                className={`h-6 px-2 aku-body-small-regular ${getStatusBadgeClassName(
                  detail.statusLabel,
                )}`}
              >
                {detail.statusLabel}
                {detail.isQueried ? (
                  <>
                    <span className="mx-1">•</span>
                    <span className="italic">Flagged</span>
                  </>
                ) : null}
              </Badge>
            </div>
            <BodyText className="aku-body-normal-regular text-[#383B56]">
              {detail.customerEmail}
            </BodyText>
          </div>
        </div>

        {!isReadOnly ? (
          <div className="flex flex-wrap items-center gap-5 xl:justify-end">
            <button
              type="button"
              onClick={onRejectCustomer}
              className="aku-body-normal-medium text-[#161827]"
            >
              Reject
            </button>
            <Button
              type="button"
              onClick={onEscalateCustomer}
              className="h-10 rounded-[10px] bg-[#EFF0FA] px-5 aku-body-normal-medium text-[#161827] hover:bg-[#EEF1FB]"
            >
              Escalate...
            </Button>
            <Button
              type="button"
              disabled={!canFinishReview}
              onClick={onFinishReview}
              className="h-10 rounded-[10px] bg-[#161827] px-6 aku-body-normal-medium text-white hover:bg-[#2B2F49] disabled:cursor-not-allowed disabled:bg-[#9A9CC0] disabled:text-white disabled:hover:bg-[#9A9CC0]"
            >
              Finish review
            </Button>
            {moreButtonDropDown()}
          </div>
        ) : isApprovedBusinesses ? <>
          {moreButtonDropDown()}
        </> : null}
      </div>

      <Card className="mt-4 overflow-hidden rounded-[16px] border border-[#EBEDF9] bg-[#F7F8FD] shadow-none">
        <CardContent className="grid items-start gap-x-6 gap-y-2 p-4 md:grid-cols-2 2xl:grid-cols-4">
          <DetailItem label="Business name" value={detail.businessName} />
          <DetailItem
            label="Country"
            value={
              <div className="flex items-center gap-2">
                <span
                  className={`h-4 w-4 rounded-full ${countryAccentClassNames[detail.country] ?? "bg-[#EEF1FB]"}`}
                />
                <span>{detail.country}</span>
              </div>
            }
          />
          <DetailItem
            label="Web presence"
            value={detail.webPresence}
            valueClassName="min-h-[24px] w-full min-w-0 whitespace-normal break-all aku-body-small-regular text-[#161827]"
          />
          <DetailItem
            label="Registered contact"
            value={detail.registeredContact}
          />

          <DetailItem
            label="RC number"
            value={
              <div className="flex items-center gap-2">
                <span>{detail.rcNumber}</span>
                <span className="grid h-4 w-4 place-items-center rounded-full bg-[#E8FBE8] text-[#1DCB1C]">
                  <Check size={14} color={approvedActionColor} />
                </span>
              </div>
            }
          />
          <DetailItem
            label="High-risk activities"
            value={getHighRiskSummary(detail)}
            valueClassName="min-h-[24px] w-full min-w-0 whitespace-normal break-words aku-body-small-regular text-[#161827]"
          />
          <DetailItem
            label="Registered address"
            value={truncateText(detail.registeredAddress)}
          />
          <DetailItem label="Phone number" value={detail.phoneNumber} />

          <DetailItem label="Business type" value={detail.businessType} />
          <DetailItem
            label="Preferred wallets"
            value={
              <div className="flex items-center gap-1.5">
                {detail.wallets.length > 0
                  ? detail.wallets.map((wallet) => (
                      <span
                        key={wallet}
                        className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-[#EEF1FB] px-1.5 aku-body-small-medium text-[#383B56]"
                      >
                        {wallet}
                      </span>
                    ))
                  : "N/A"}
              </div>
            }
          />
          <DetailItem
            label="Physical address"
            value={truncateText(detail.physicalAddress)}
          />
          <DetailItem label="Registered" value={detail.registrationDate} />
        </CardContent>
      </Card>
    </section>
  );
};

export default KybComplianceShowTopSection;
